import { Injectable, Inject } from '@angular/core';
import {
  SelectedCustomerService,
  ProductService,
  CustomerProductPrice,
  CartRowPrice,
  CartRow,
  Price,
  GungFlowService,
  NavigationConfig
} from 'gung-standard';
import { HttpClient } from '@angular/common/http';
import { JeevesPriceService } from 'gung-standard-jeeves';
import { switchMap, first, map } from 'rxjs';
import { forkJoin, of, Observable } from 'rxjs';
import { getAddonsByParentProduct, flattenArray } from '../utilities/cart-rows-extra-configure-helpers';
import { ColmecCustomerProductPrice } from '../models/colmec-price';

@Injectable({
  providedIn: 'root'
})
export class ColmecPriceService extends JeevesPriceService {
  constructor(
    http: HttpClient,
    customerService: SelectedCustomerService,
    productService: ProductService,
    gungFlowService: GungFlowService,
    @Inject('environment')
    navigationConfig: NavigationConfig
  ) {
    super(http, customerService, productService, gungFlowService, navigationConfig);
  }

  public getCartRowPriceObservable(cartRow: CartRow): Observable<CartRowPrice> {
    // In case the cart row extra contains configure
    // then get the customer price for all items under configure and sum the cart row prices with the customer price of those items
    if (cartRow.extra.configure) {
      // get all info under configure
      const addonsByParentProduct = getAddonsByParentProduct([cartRow]);

      // flatten array of all addonids
      const allAddonIds: string[] = flattenArray(addonsByParentProduct.map(x => x.addonsIds));

      return this.customerService.getSelectedCustomer().pipe(
        switchMap(customer =>
          forkJoin([
            this.getCustomerPrices(allAddonIds, customer.id).pipe(first()),
            super.getCartRowPriceObservable(cartRow).pipe(first())
          ])
        ),
        map(([customerProductPriceAddons, cartRowPrice]) => {
          // go through all addons for the product and sum them to the current product prices
          for (const customerProductPriceAddon of customerProductPriceAddons) {
            cartRowPrice = this.addCustomerAddonProductPriceToCartRowPrice(
              customerProductPriceAddon,
              cartRowPrice,
              cartRow
            );
          }

          return cartRowPrice;
        })
      );
    } else {
      return super.getCartRowPriceObservable(cartRow);
    }
  }

  public getCartRowPricesObservable(cartRows: CartRow[]): Observable<CartRowPrice[]> {
    // In case there is any cart row that contains extra configure data
    // then get the customer price for those items under configure and sum the cart row prices with the customer price of the addon
    if (cartRows.some(row => row.extra.configure)) {
      // get all info under configure
      const addonsByParentProducts = getAddonsByParentProduct(cartRows);

      // flatten array of all addonids
      const allAddonIds: string[] = flattenArray(addonsByParentProducts.map(x => x.addonsIds));

      return this.customerService.getSelectedCustomer().pipe(
        switchMap(customer =>
          forkJoin([
            of(addonsByParentProducts),
            this.getCustomerPrices(allAddonIds, customer.id).pipe(first()),
            super.getCartRowPricesObservable(cartRows).pipe(first())
          ])
        ),
        map(([addonsByParentCartRowProducts, customerProductPriceAddons, cartRowPrices]) => {
          const cartRowsPricesWithAddonPrices: CartRowPrice[] = [];

          // Go throug all the cart rows
          // If there is any addons in the cartRow which the cartRowPrice belong to
          // then add the customer price of the addon
          // otherwise add the cartRowPrice
          for (const cartRowPrice of cartRowPrices) {
            const cartRow = cartRows.find(row => row.productId === cartRowPrice.productId);

            // items under configure for the product of the current cart row
            const addonsByParentCurrentCartRowProduct = addonsByParentCartRowProducts.find(
              x => x.parentProductId === cartRowPrice.productId
            );

            if (addonsByParentCurrentCartRowProduct) {
              let updatedCartRowPrice = cartRowPrice;

              // go through all addons for the current cart
              for (const addonsIdsCartRowProduct of addonsByParentCurrentCartRowProduct.addonsIds) {
                const customerProductPriceAddon = customerProductPriceAddons.find(
                  x => x.productId === addonsIdsCartRowProduct
                );
                updatedCartRowPrice = this.addCustomerAddonProductPriceToCartRowPrice(
                  customerProductPriceAddon,
                  cartRowPrice,
                  cartRow
                );
              }
              cartRowsPricesWithAddonPrices.push(updatedCartRowPrice);
            } else {
              cartRowsPricesWithAddonPrices.push(cartRowPrice);
            }
          }

          return cartRowsPricesWithAddonPrices;
        })
      );
    } else {
      return super.getCartRowPricesObservable(cartRows);
    }
  }

  private addCustomerAddonProductPriceToCartRowPrice(
    customerAddonProductPrice: CustomerProductPrice,
    cartRowPrice: CartRowPrice,
    cartRow: CartRow
  ): CartRowPrice {
    // Unit price
    cartRowPrice.cartRowUnitPrice = {
      ...cartRowPrice.cartRowUnitPrice,
      value: cartRowPrice.cartRowUnitPrice.value + customerAddonProductPrice.customerGrossPrice.value
    };

    // Unit Proce Include Row Discount
    cartRowPrice.cartRowUnitPriceInclRowDiscount = {
      ...cartRowPrice.cartRowUnitPriceInclRowDiscount,
      value: cartRowPrice.cartRowUnitPriceInclRowDiscount.value + customerAddonProductPrice.customerNetPrice.value
    };

    // Calculate again Total Price
    cartRowPrice.cartRowTotalPrice = {
      ...cartRowPrice.cartRowTotalPrice,
      value: cartRowPrice.cartRowUnitPrice.value * cartRow.qty
    };

    // Calculate again total price with discount
    cartRowPrice.cartRowTotalPriceInclRowDiscount = {
      ...cartRowPrice.cartRowTotalPriceInclRowDiscount,
      value: cartRowPrice.cartRowUnitPriceInclRowDiscount.value * cartRow.qty
    };

    return cartRowPrice;
  }

  getCurrentCustomerPrice(productId: string): Observable<ColmecCustomerProductPrice> {
    return forkJoin({
      customerProductPrice: super.getCurrentCustomerPrice(productId).pipe(first()),
      product: this.productService.getProduct(productId).pipe(first())
    }).pipe(
      first(),
      switchMap(result => {
        // In Case contains autoAdded then add get the autoAdded customerProductPrices
        // and sum it to the customerNetPrice in a new property priceInclAutoAdded
        if (
          result.product.extra.ar.tillvalartikelauto &&
          result.product.extra.artv &&
          result.product.extra.artv.length !== 0 &&
          result.product.extra.artv[0].artnrtillvalart
        ) {
          return forkJoin({
            customerProductPrice: of(result.customerProductPrice),
            customerProductPriceAutoAdded: super
              .getCurrentCustomerPrice(result.product.extra.artv[0].artnrtillvalart)
              .pipe(first())
          }).pipe(
            first(),
            map(data => {
              return {
                ...data.customerProductPrice,
                priceInclAutoAdded: {
                  value:
                    data.customerProductPrice.customerNetPrice.value +
                    data.customerProductPriceAutoAdded.customerNetPrice.value,
                  currencyCode: data.customerProductPrice.customerNetPrice.currencyCode
                }
              };
            })
          );
        } else {
          return of(result.customerProductPrice);
        }
      })
    );
  }

  getCurrentCustomerPrices(productIds: string[]): Observable<CustomerProductPrice[]> {
    return forkJoin({
      customerProductsPrices: super.getCurrentCustomerPrices(productIds).pipe(first()),
      products: this.productService.getFullProductsByIds(productIds).pipe(first())
    }).pipe(
      first(),
      switchMap(result => {
        // In Case any contains autoAdded then add get the autoAdded customerProductsPrices
        // and sum it to the customerNetPrice in a new property priceInclAutoAdded
        const setAutoAddedIds = new Set<string>(
          result.products
            .filter(
              product =>
                product.extra.ar.tillvalartikelauto &&
                product.extra.artv &&
                product.extra.artv.length !== 0 &&
                product.extra.artv[0].artnrtillvalart
            )
            .map(product => product.extra.artv[0].artnrtillvalart as string)
        );

        const autoAddedIds: string[] = Array.from(setAutoAddedIds);

        if (autoAddedIds && autoAddedIds.length > 0) {
          return forkJoin({
            customerProductsPrices: of(result.customerProductsPrices),
            products: of(result.products),
            customerProductsPricesAutoAdded: super.getCurrentCustomerPrices(autoAddedIds).pipe(first())
          }).pipe(
            first(),
            map(data => {
              const colmecCustomerProductsPrices: ColmecCustomerProductPrice[] = [];

              data.customerProductsPrices.forEach(customerProductPrice => {
                // get product
                const customerProduct = data.products.find(product => product.id === customerProductPrice.productId);

                // if contains autoAdded get it and then sum the autoAdded
                if (
                  customerProduct?.extra?.artv &&
                  customerProduct.extra.artv.length !== 0 &&
                  customerProduct.extra.artv[0].artnrtillvalart
                ) {
                  const autoAddedId = customerProduct.extra.artv[0].artnrtillvalart as string;
                  const customerProductPriceAutoAdded = data.customerProductsPricesAutoAdded.find(
                    x => x.productId === autoAddedId
                  );

                  colmecCustomerProductsPrices.push({
                    ...customerProductPrice,
                    priceInclAutoAdded: {
                      value:
                        customerProductPrice.customerNetPrice.value +
                        (customerProductPriceAutoAdded?.customerNetPrice.value || 0),
                      currencyCode: customerProductPrice.customerNetPrice.currencyCode
                    }
                  });
                } else {
                  colmecCustomerProductsPrices.push(customerProductPrice);
                }
              });

              return colmecCustomerProductsPrices;
            })
          );
        } else {
          return of(result.customerProductsPrices);
        }
      })
    );
  }
}
